import { useCallback, useState } from "react";

import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentRoutesService } from "../services/paymentRoutes.service";
import type { CreatePaymentRoutePayload, ProviderRailRoute } from "../types/paymentRoutes.types";

/**
 * What `DELETE` did. `missing` is a 404 — someone else already removed the row, so a refresh is the right
 * outcome, not an error.
 */
export type RemoveRouteOutcome = "deleted" | "missing" | "error";

export interface UseRouteMutationsResult {
  /** True while a create or delete is in flight — the owning dialog must block closing and double-submits. */
  isSubmitting: boolean;
  /**
   * Normalized user-facing message of the last failed mutation; cleared on `clearError` and on every new
   * attempt.
   */
  error: string | null;
  clearError: () => void;
  /** `POST /internal/payments/routes`. Resolves the created row, or null after a failure (`error` is set). */
  createRoute: (payload: CreatePaymentRoutePayload) => Promise<ProviderRailRoute | null>;
  /** `DELETE /internal/payments/routes/:id`. Never throws — the outcome says what happened. */
  removeRoute: (id: string) => Promise<RemoveRouteOutcome>;
}

/**
 * The routes write path — the API's only two mutations (there is no PUT/PATCH). Owns the HTTP calls, the
 * in-flight flag and the error normalization so the dialogs stay presentational (no API calls in components).
 * Each dialog mounts its own instance; nothing here is shared state.
 */
export function useRouteMutations(): UseRouteMutationsResult {
  const { t } = useTranslations("paymentRoutes");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createRoute = useCallback(
    async (payload: CreatePaymentRoutePayload): Promise<ProviderRailRoute | null> => {
      setIsSubmitting(true);
      setError(null);
      try {
        return await PaymentRoutesService.create(payload);
      } catch (caught: unknown) {
        setError(
          HttpApiService.isAccessDeniedError(caught)
            ? t("error_no_write_permission")
            : // The backend's own message is the useful one here: it names the `route_exists` collision precisely.
              HttpApiService.getErrorMessage(caught) || t("error_create"),
        );
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [t],
  );

  const removeRoute = useCallback(
    async (id: string): Promise<RemoveRouteOutcome> => {
      setIsSubmitting(true);
      setError(null);
      try {
        await PaymentRoutesService.remove(id);
        return "deleted";
      } catch (caught: unknown) {
        if (HttpApiService.isNotFoundError(caught)) return "missing";
        setError(
          HttpApiService.isAccessDeniedError(caught)
            ? t("error_no_write_permission")
            : HttpApiService.getErrorMessage(caught) || t("error_delete"),
        );
        return "error";
      } finally {
        setIsSubmitting(false);
      }
    },
    [t],
  );

  return {
    isSubmitting,
    error,
    clearError: useCallback(() => setError(null), []),
    createRoute,
    removeRoute,
  };
}
